import { Controller } from "@hotwired/stimulus"

const DRAFT_KEY = "chat_input_draft"

export default class extends Controller {
    static identifier = "chat"
    static targets = ["input", "suggestions", "form"]
    static values = {
        settingsPath: String
    }

    sanitizeChatText(text) {
        // Keep client behavior aligned with server-side filtering.
        return (text || "").replace(/[\u00AD\u200B\u2060\uFEFF]/g, "")
    }

    maxLength() {
        if (!this.hasInputTarget) return 0
        const raw = this.inputTarget.getAttribute("maxlength")
        const parsed = parseInt(raw || "0", 10)
        return Number.isFinite(parsed) ? parsed : 0
    }

    handlePaste(event) {
        if (!this.hasInputTarget) return
        if (!event.clipboardData) return

        const pasted = event.clipboardData.getData("text")
        if (!pasted) return

        const sanitized = this.sanitizeChatText(pasted)
        const max = this.maxLength()

        // If there's nothing to sanitize or enforce, let the browser handle it.
        if (sanitized === pasted && max <= 0) return

        event.preventDefault()

        const current = this.inputTarget.value || ""
        const start = this.inputTarget.selectionStart ?? current.length
        const end = this.inputTarget.selectionEnd ?? current.length
        const before = current.slice(0, start)
        const after = current.slice(end)

        let insert = sanitized
        if (max > 0) {
            const remaining = max - (before.length + after.length)
            if (remaining <= 0) {
                insert = ""
            } else if (insert.length > remaining) {
                insert = insert.slice(0, remaining)
            }
        }

        this.inputTarget.value = before + insert + after
        const newPos = before.length + insert.length
        this.inputTarget.setSelectionRange(newPos, newPos)

        this.inputChanged()
    }

    connect() {
        this.element.addEventListener("turbo:submit-end", (event) => {
            if (event.detail.success) {
                this.clear()
            }
        })

        // Available slash commands
        const settingsPath = this.hasSettingsPathValue ? this.settingsPathValue : "/settings"

        this.commands = [
            { command: "/process", description: "Main processing area", path: "/dig" },
            { command: "/upgrades", description: "Upgrade systems", path: "/upgrades" },
            { command: "/performance", description: "Performance leaderboards", path: "/legends" },
            { command: "/recursion", description: "Recursion interface", path: "/recursion" },
            { command: "/settings", description: "System settings", path: settingsPath },
            { command: "/shop", description: "DeepCo™ shop", path: "/shop" },
            { command: "/achievements", description: "Achievement system", path: "/achievements" },
            { command: "/departments", description: "Department management", path: "/departments" },
            { command: "/messages", description: "Email system", path: "/worker_emails" },
            { command: "/terminal", description: "Access Terminal (TUI)", path: "/terminal" },
            { command: "/faq", description: "Frequently asked questions", path: "/faq" },
            { command: "/terms", description: "Terms of service", path: "/terms" },
            { command: "/syslog", description: "System update log", path: "/dev_log_entries" },
            { command: "/idle", description: "Async Division", path: "/idle/initiate" }
        ]

        // Available exclamation commands
        this.exclamationCommands = [
            { command: "!feedback", description: "Submit feedback to DeepCo™ administration", isSpecial: true }
        ]

        try {
            const draft = sessionStorage.getItem(DRAFT_KEY)
            if (draft && this.hasInputTarget) {
                this.inputTarget.value = draft
                this.inputChanged()
            }
        } catch (_error) {
            // Ignore storage errors (e.g., private browsing)
        }
    }

    inputChanged() {
        if (!this.hasInputTarget) return

        const value = this.inputTarget.value

        try {
            if (value) {
                sessionStorage.setItem(DRAFT_KEY, value)
            } else {
                sessionStorage.removeItem(DRAFT_KEY)
            }
        } catch (_error) {
            // Ignore storage errors (e.g., private browsing)
        }

        // Handle !feedback confirmation
        if (this.hasFormTarget) {
            if (value.toLowerCase().trim().startsWith("!feedback")) {
                // Extract the feedback message (everything after !feedback)
                const feedbackMessage = value.substring(9).trim()
                // Only show confirmation if there's actual feedback content
                if (feedbackMessage.length > 0) {
                    this.formTarget.setAttribute("data-turbo-confirm", "This is actual feedback to the developer. Are you sure you want to send this?")
                } else {
                    this.formTarget.removeAttribute("data-turbo-confirm")
                }
            } else {
                this.formTarget.removeAttribute("data-turbo-confirm")
            }
        }

        if (value.startsWith("/")) {
            this.showSuggestions(value, this.commands)
        } else if (value.startsWith("!")) {
            this.showSuggestions(value, this.exclamationCommands)
        } else {
            this.hideSuggestions()
        }
    }

    showSuggestions(input, commandList) {
        const query = input.toLowerCase()
        const matches = commandList.filter(cmd =>
            cmd.command.toLowerCase().startsWith(query) ||
            cmd.description.toLowerCase().includes(query.slice(1))
        )

        if (matches.length > 0 && this.hasSuggestionsTarget) {
            this.renderSuggestions(matches)
            this.suggestionsTarget.classList.remove('hidden')
            // Auto-select the first suggestion
            this.selectSuggestion(0)
        } else {
            this.hideSuggestions()
        }
    }

    renderSuggestions(matches) {
        if (!this.hasSuggestionsTarget) return

        const html = matches.map((match, index) => `
            <li class="w-full">
              <button type="button"
                      class="chat-suggestion-btn btn btn-ghost btn-sm w-full justify-between gap-3 font-mono text-xs"
                      data-command="${match.command}"
                      data-path="${match.path || ''}"
                      data-is-special="${match.isSpecial || false}"
                      data-index="${index}">
                <span class="font-semibold text-primary">${match.command}</span>
                <span class="text-[0.7rem] tracking-wide text-base-content/70">${match.description}</span>
              </button>
            </li>
        `).join("")

        this.suggestionsTarget.innerHTML = html

        // Add click handlers
        this.suggestionsTarget.querySelectorAll('.chat-suggestion-btn').forEach(el => {
            el.addEventListener('click', (e) => {
                const isSpecial = e.currentTarget.dataset.isSpecial === 'true'
                if (isSpecial) {
                    // For special commands like !feedback, just fill the input
                    this.inputTarget.value = e.currentTarget.dataset.command + ' '
                    this.inputTarget.focus()
                    this.hideSuggestions()
                } else {
                    this.executeCommand(e.currentTarget.dataset.path)
                }
            })
        })
    }

    hideSuggestions() {
        if (this.hasSuggestionsTarget) {
            this.suggestionsTarget.classList.add('hidden')
            // Clear any selected suggestions
            this.suggestionsTarget.querySelectorAll('.chat-suggestion-btn').forEach(s => {
                s.classList.remove('btn-active')
            })
        }
    }

    executeCommand(path) {
        // Navigate to the command path
        window.location.href = path
    }

    handleKeydown(event) {
        if (!this.hasInputTarget) return

        const value = this.inputTarget.value
        const isSlashCommand = value.startsWith("/")
        const isExclamationCommand = value.startsWith("!")

        // Handle Enter key for slash commands
        if (event.key === "Enter" && isSlashCommand) {
            // First check if there's a selected suggestion (prioritize highlighted selection)
            if (this.hasSuggestionsTarget && !this.suggestionsTarget.classList.contains('hidden')) {
                const selected = this.suggestionsTarget.querySelector('.chat-suggestion-btn.btn-active')
                if (selected) {
                    event.preventDefault()
                    this.executeCommand(selected.dataset.path)
                    return
                }
            }

            // If no suggestion is selected, try exact command match
            const command = this.commands.find(cmd =>
                cmd.command.toLowerCase() === value.toLowerCase().trim()
            )

            if (command) {
                event.preventDefault()
                this.executeCommand(command.path)
                return
            }
        }

        // Handle arrow keys for suggestion navigation
        if ((isSlashCommand || isExclamationCommand) && this.hasSuggestionsTarget && !this.suggestionsTarget.classList.contains('hidden')) {
            const suggestions = this.suggestionsTarget.querySelectorAll('.chat-suggestion-btn')
            if (suggestions.length === 0) return

            const current = this.suggestionsTarget.querySelector('.chat-suggestion-btn.btn-active')
            let currentIndex = current ? parseInt(current.dataset.index) : -1

            if (event.key === "ArrowDown") {
                event.preventDefault()
                currentIndex = currentIndex < suggestions.length - 1 ? currentIndex + 1 : 0
                this.selectSuggestion(currentIndex)
            } else if (event.key === "ArrowUp") {
                event.preventDefault()
                currentIndex = currentIndex > 0 ? currentIndex - 1 : suggestions.length - 1
                this.selectSuggestion(currentIndex)
            } else if (event.key === "Tab") {
                event.preventDefault()
                if (current) {
                    const isSpecial = current.dataset.isSpecial === 'true'
                    this.inputTarget.value = current.dataset.command + (isSpecial ? ' ' : '')
                    this.hideSuggestions()
                }
            } else if (event.key === "Escape") {
                event.preventDefault()
                this.hideSuggestions()
            }
        }
    }

    selectSuggestion(index) {
        if (!this.hasSuggestionsTarget) return

        const suggestions = this.suggestionsTarget.querySelectorAll('.chat-suggestion-btn')
        suggestions.forEach(s => s.classList.remove('btn-active'))

        if (suggestions[index]) {
            suggestions[index].classList.add('btn-active')
            // Scroll the selected suggestion into view
            this.scrollSuggestionIntoView(suggestions[index])
        }
    }

    scrollSuggestionIntoView(suggestionElement) {
        if (!suggestionElement || !this.hasSuggestionsTarget) return

        const container = this.suggestionsTarget
        const containerRect = container.getBoundingClientRect()
        const elementRect = suggestionElement.getBoundingClientRect()

        // Check if element is above the visible area
        if (elementRect.top < containerRect.top) {
            container.scrollTop -= (containerRect.top - elementRect.top)
        }
        // Check if element is below the visible area
        else if (elementRect.bottom > containerRect.bottom) {
            container.scrollTop += (elementRect.bottom - containerRect.bottom)
        }
    }

    clear() {
        try { sessionStorage.removeItem(DRAFT_KEY) } catch (_error) { /* ignore */ }
        if (this.hasInputTarget) {
            this.inputTarget.value = ""
        }
        if (this.hasFormTarget) {
            this.formTarget.removeAttribute("data-turbo-confirm")
        }
        this.hideSuggestions()
    }
}
